import React from 'react'
import { useDispatch } from 'react-redux'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import { useGetDeviceLinks } from 'src/Libs/installations/hooks'
import { isFlowTheme } from 'src/Libs/utils'
import { useTypedSelector } from 'src/State/RootReducer'
import { setActiveInstallation } from 'src/State/Storage/StorageActions'
import { buildNavigationUrl, normalizePath } from './utils/build-navigation-url'

interface Props {
  children: React.ReactNode
  allowAccessWithoutStorageId?: boolean
}

/**
 * Route guard that automatically injects the storageId in case its missing in the URL.
 *
 * Usage: wrap route elements whose path contains :storageId? with this component.
 * @param shouldFallbackToChildren boolean - will show the children even if no storageId is found in the device links (eg. Dashboard access for installations under construction)
 */
const RequireStorageId: React.FC<Props> = ({ children, allowAccessWithoutStorageId: shouldFallbackToChildren }) => {
  const { installationId, storageId: storageIdParam } = useParams<{ installationId: string; storageId?: string }>()
  // Convert to number when the param looks like a numeric storage id
  const storageIdNumber = Number.isNaN(Number(storageIdParam)) ? undefined : Number(storageIdParam)
  const activeStorage = useTypedSelector(state => state.storage.activeStorage)
  const dispatch = useDispatch()
  const location = useLocation()
  const base = normalizePath(location.pathname)

  // prefer explicit storageId param from url, otherwise fall back to active storage from state
  const currentStorageId = storageIdNumber ?? activeStorage
  const { resolvedStorageId } = useDeriveResolvedStorageIdFromDeviceLinks(installationId, storageIdNumber)

  if (installationId && !storageIdNumber) {
    const storageIdToRedirect = currentStorageId || resolvedStorageId

    if (storageIdToRedirect) {
      // Make sure to update redux, if page was opened from a bookmark/link
      dispatch(setActiveInstallation(Number.parseInt(installationId), storageIdToRedirect?.toString()))
      const navigationUrl = buildNavigationUrl(base, installationId, storageIdToRedirect, location.search)
      return <Navigate to={navigationUrl} replace />
    }

    // No useable storageId was found for installation → check if the page is accessible without it (eg. Dashboard)
    if (shouldFallbackToChildren) return <>{children}</>

    return <Navigate to="/not-found" replace />
  }

  return <>{children}</>
}

export default RequireStorageId

/**
 * Get the storageId from fetched deviceLinks → only if storageID is present
 */
const useDeriveResolvedStorageIdFromDeviceLinks = (
  installationId?: string,
  storageId?: number
): { resolvedStorageId?: string; isLoadingDeviceLinks: boolean } => {
  // If storageId is available, don't fetch device links for the installation
  const installationIdToFetch = storageId ? undefined : installationId

  const { deviceLinks, isLoading: isLoadingDeviceLinks } = useGetDeviceLinks(installationIdToFetch)
  const filter = isFlowTheme ? 'emc' : 'storage'
  const resolvedStorageId = Array.isArray(deviceLinks) ? deviceLinks.find(link => link.deviceType === filter)?.deviceId : undefined

  return { resolvedStorageId: resolvedStorageId ?? undefined, isLoadingDeviceLinks }
}
